import { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';

const VERSION_CHECK_INTERVAL = 60 * 60 * 1000; // Check every 1 hour
const DISMISSED_VERSION_KEY = 'dismissedVersion';

interface VersionInfo {
  version: string;
  buildTime: string;
}

export const useVersionCheck = () => {
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [newVersion, setNewVersion] = useState<VersionInfo | null>(null);
  const currentVersionRef = useRef<string | null>(null);
  const intervalRef = useRef<number | null>(null);

  const checkVersion = async () => {
    try {
      const backendVersion = await api.getVersion();

      // On first check, store current version
      if (!currentVersionRef.current) {
        currentVersionRef.current = backendVersion.version;
        return;
      }

      // Check if version changed
      if (backendVersion.version !== currentVersionRef.current) {
        // Check if user already dismissed this version
        const dismissedVersion = localStorage.getItem(DISMISSED_VERSION_KEY);

        if (dismissedVersion !== backendVersion.version) {
          setNewVersion(backendVersion);
          setShowUpdateModal(true);
        }
      }
    } catch (error) {
      console.error('Failed to check version:', error);
    }
  };

  const handleUpdate = () => {
    // Clear dismissed version and reload
    localStorage.removeItem(DISMISSED_VERSION_KEY);
    window.location.reload();
  };

  const handleDismiss = () => {
    if (newVersion) {
      // Store dismissed version so we don't show again until next version
      localStorage.setItem(DISMISSED_VERSION_KEY, newVersion.version);
    }
    setShowUpdateModal(false);
  };

  useEffect(() => {
    // Check immediately on mount
    checkVersion();

    // Set up periodic checking
    intervalRef.current = window.setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    showUpdateModal,
    handleUpdate,
    handleDismiss,
  };
};
